<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onScopeDispose,
    ref,
    shallowRef,
    watch,
  } from 'vue';
  import { useNow } from '@vueuse/core';
  import { ReservationsService } from '@/api/services/reservations.service';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { buildPosTableViews } from '@/domain/pos/table-views';
  import {
    addDays,
    formatTime,
    getReservationTimeZone,
    toDateKey,
  } from '@/domain/reservations/reservation-time';
  import {
    computeTableAvailability,
    requiresForcedTableSelection,
  } from '@/domain/reservations/table-availability';
  import type { ITableReservationAvailability } from '@/domain/reservations/table-availability';
  import { OrderType } from '@/enums/OrderEnums';
  import type {
    IReservation,
    IReservationsSettingsSummary,
  } from '@/interfaces/IReservation';
  import type { IReservationBookingContext } from '@/interfaces/IReservationSettings';
  import type {
    IReservationTablePickerInventory,
    IReservationTablePickerValidity,
  } from '@/interfaces/IReservationUi';
  import { useOrdersStore } from '@/stores/orders.store';
  import { useReservationsStore } from '@/stores/reservations.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  const props = withDefaults(
    defineProps<{
      value?: number[];
      partySize: number;
      startsAt: string;
      durationMin?: number;
      excludeReservationId?: number;
      mode?: 'reservation' | 'now';
      disabled?: boolean;
      multiple?: boolean;
      /** null means caller-owned inventory is loading; undefined enables the private loader. */
      inventory?: IReservationTablePickerInventory | null;
    }>(),
    {
      value: () => [],
      durationMin: undefined,
      excludeReservationId: undefined,
      mode: 'reservation',
      disabled: false,
      multiple: true,
      inventory: undefined,
    }
  );
  const emit = defineEmits<{
    (event: 'input', value: number[]): void;
    (event: 'select', value: number[]): void;
    (event: 'availability', value: ITableReservationAvailability[]): void;
    (event: 'validity', value: IReservationTablePickerValidity): void;
  }>();
  const restaurant = useRestaurantStore();
  const orders = useOrdersStore();
  const store = useReservationsStore();
  const modals = useModal();
  const ui = useUi();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const now = useNow({ interval: 30000 });
  const loading = ref(false);
  const failed = ref(false);
  const choosing = ref(false);
  const sectorId = ref('');
  const reservations = shallowRef<IReservation[]>([]);
  const settings = shallowRef<IReservationsSettingsSummary | null>(null);
  const bookingContext = shallowRef<IReservationBookingContext | null>(null);
  const effectiveContext = computed(
    () => props.inventory?.bookingContext ?? bookingContext.value
  );
  const effectiveSettings = computed(
    () => props.inventory?.settings ?? settings.value
  );
  let sequence = 0;
  let alive = true;
  onScopeDispose(() => {
    alive = false;
    sequence++;
  });
  const zone = computed(() =>
    getReservationTimeZone(
      restaurant.selectedBranch?.location,
      restaurant.restaurant?.country
    )
  );
  const validInstant = computed(() =>
    Number.isFinite(Date.parse(props.startsAt))
  );
  const date = computed(() =>
    validInstant.value ? toDateKey(props.startsAt, zone.value) : null
  );
  const sectors = computed(() =>
    [...(restaurant.sectors ?? [])].sort((a, b) => a.index - b.index)
  );
  const views = computed(
    () =>
      buildPosTableViews(
        restaurant.tables?.filter(
          (table) => table.branchId === restaurant.selectedBranch?.id
        ),
        orders.pendingOrdersByType?.[OrderType.Seat] ?? [],
        ''
      ) ?? []
  );
  const choices = computed(() =>
    validInstant.value
      ? computeTableAvailability({
          tables: views.value,
          reservations: props.inventory?.reservations ?? reservations.value,
          startsAt: props.startsAt,
          durationMin:
            props.durationMin ??
            effectiveContext.value?.settings.durationRules.find(
              (rule) => props.partySize <= rule.maxParty
            )?.minutes ??
            effectiveSettings.value?.defaultDurationMin ??
            90,
          partySize: props.partySize,
          bufferMin: effectiveSettings.value?.bufferMin ?? 0,
          now: now.value,
          mode: props.mode,
          excludeReservationId: props.excludeReservationId,
        }).sort((a, b) => {
          const aSector = sectors.value.findIndex(
            (sector) => sector.id === a.table.sectorId
          );
          const bSector = sectors.value.findIndex(
            (sector) => sector.id === b.table.sectorId
          );
          return (
            aSector - bSector ||
            a.table.name.localeCompare(b.table.name, undefined, {
              numeric: true,
            })
          );
        })
      : []
  );
  const visibleChoices = computed(() =>
    choices.value.filter(
      (choice) =>
        !sectorId.value || choice.table.sectorId === Number(sectorId.value)
    )
  );
  const selected = computed(() =>
    choices.value.filter((choice) => props.value.includes(choice.table.id))
  );
  const validity = computed<IReservationTablePickerValidity>(() => ({
    loading: loading.value,
    valid:
      !loading.value &&
      !failed.value &&
      !!effectiveContext.value &&
      props.value.length > 0 &&
      props.value.length <= 6 &&
      selected.value.length === props.value.length &&
      selected.value.every((choice) => choice.selectable),
    force: requiresForcedTableSelection(
      selected.value,
      props.partySize,
      effectiveContext.value?.tableGroups ?? []
    ),
  }));
  watch(choices, (value) => emit('availability', value), { immediate: true });
  watch(validity, (value) => emit('validity', value), { immediate: true });

  async function load() {
    const request = ++sequence;
    if (props.inventory !== undefined) {
      loading.value = props.inventory === null;
      failed.value = false;
      return;
    }
    const restaurantId = restaurant.restaurant?.id;
    const branchId = restaurant.selectedBranch?.id;
    const day = date.value;
    if (!restaurantId || !branchId || !day) {
      reservations.value = [];
      loading.value = false;
      return;
    }
    loading.value = true;
    failed.value = false;
    try {
      // Long overnight shifts plus a buffered hold can reach two service dates
      // away from this wall date; use the same inventory window as the API.
      const [snapshots, context] = await Promise.all([
        Promise.all(
          [-2, -1, 0, 1, 2]
            .map((offset) => addDays(day, offset))
            .map((value) =>
              ReservationsService.getDay(restaurantId, branchId, value)
            )
        ),
        ReservationsService.getBookingContext(restaurantId, branchId),
      ]);
      if (!alive || request !== sequence) return;
      reservations.value = [
        ...new Map(
          snapshots
            .flatMap((value) => value.reservations)
            .map((row) => [row.id, row])
        ).values(),
      ];
      settings.value = snapshots[2].settingsSummary;
      bookingContext.value = context;
    } catch (error) {
      if (!alive || request !== sequence) return;
      failed.value = true;
      ui.serverError(error, t('reservations.page.loadError'));
    } finally {
      if (alive && request === sequence) loading.value = false;
    }
  }
  watch(
    () => [
      date.value,
      restaurant.restaurant?.id,
      restaurant.selectedBranch?.id,
      store.revision,
      props.inventory,
    ],
    () => void load(),
    { immediate: true }
  );

  async function choose(choice: ITableReservationAvailability) {
    if (
      props.disabled ||
      choosing.value ||
      loading.value ||
      failed.value ||
      !choice.selectable
    )
      return;
    const request = sequence;
    choosing.value = true;
    try {
      const removing = props.multiple && props.value.includes(choice.table.id);
      if (!removing && props.multiple && props.value.length >= 6) return;
      if (!removing && choice.requiresConfirmation) {
        const accepted = await modals.confirm({
          title: t('reservations.tablePicker.reserved'),
          message: t('reservations.tablePicker.reservedConfirm', {
            time: choice.reservation
              ? formatTime(choice.reservation.startsAt, zone.value)
              : '',
          }),
          acceptText: t('confirm'),
          cancelText: t('cancel'),
        });
        if (!accepted || !alive || request !== sequence) return;
      }
      const value = removing
        ? props.value.filter((id) => id !== choice.table.id)
        : props.multiple
        ? [...props.value, choice.table.id]
        : [choice.table.id];
      emit('input', value);
      if (alive) emit('select', value);
    } finally {
      if (alive) choosing.value = false;
    }
  }
  function statusLabel(choice: ITableReservationAvailability) {
    return t(
      `reservations.tablePicker.${
        choice.status === 'too_small' ? 'tooSmall' : choice.status
      }`
    );
  }
</script>

<template>
  <div class="reservation-table-picker">
    <div class="picker-toolbar">
      <label class="sector-field">
        <span>{{ $t('reservations.form.sector') }}</span>
        <select
          v-model="sectorId"
          class="custom-input"
          :disabled="disabled || loading"
        >
          <option value="">{{ $t('reservations.form.sectorAny') }}</option>
          <option
            v-for="sector in sectors"
            :key="sector.id"
            :value="String(sector.id)"
          >
            {{ sector.name }}
          </option>
        </select>
      </label>
      <span class="selection-count">{{
        $t('reservations.tablePicker.selected', { count: value.length })
      }}</span>
    </div>
    <div v-if="loading" class="tables-grid" aria-busy="true">
      <PosUiSkeleton v-for="index in 6" :key="index" class="table-skeleton" />
    </div>
    <PosUiEmptyState
      v-else-if="failed"
      icon="cloud-alert"
      :title="$t('reservations.page.loadError')"
    >
      <template #action
        ><PosUiButton color="neutral" variant="outline" @click="load">{{
          $t('reservations.page.retry')
        }}</PosUiButton></template
      >
    </PosUiEmptyState>
    <div v-else-if="visibleChoices.length" class="tables-grid">
      <button
        v-for="choice in visibleChoices"
        :key="choice.table.id"
        type="button"
        class="table-choice"
        :class="[choice.status, { selected: value.includes(choice.table.id) }]"
        :disabled="disabled || choosing || !choice.selectable"
        :aria-pressed="value.includes(choice.table.id)"
        @click="choose(choice)"
      >
        <span class="table-name"
          >{{ $t('reservations.common.table', { name: choice.table.name })
          }}<PosUiIcon
            v-show="value.includes(choice.table.id)"
            name="check-circle"
        /></span>
        <span class="table-capacity">{{
          $tc('reservations.common.people', choice.table.capacity, {
            count: choice.table.capacity,
          })
        }}</span>
        <span class="table-status"
          >{{ statusLabel(choice)
          }}<template v-if="choice.reservation && choice.status === 'reserved'">
            · {{ formatTime(choice.reservation.startsAt, zone) }}</template
          ></span
        >
      </button>
    </div>
    <PosUiEmptyState
      v-else
      icon="table-chair"
      :title="$t('reservations.tablePicker.emptyTitle')"
      :description="$t('reservations.tablePicker.emptyDescription')"
    />
    <p v-show="multiple" class="picker-hint">
      {{ $t('reservations.tablePicker.multiHint') }}
    </p>
  </div>
</template>

<style scoped lang="scss">
  .reservation-table-picker {
    @apply tw-flex tw-flex-col tw-gap-3;
    .picker-toolbar {
      @apply tw-flex tw-items-end tw-justify-between tw-gap-3;
    }
    .sector-field {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1 tw-text-xs tw-font-medium tw-text-neutral-500;
    }
    .custom-input {
      @apply tw-min-h-[44px] tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-3 tw-text-sm tw-text-neutral-900;
      &:focus {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .selection-count,
    .picker-hint {
      @apply tw-text-xs tw-text-neutral-500;
    }
    .tables-grid {
      @apply tw-grid tw-gap-2;
      grid-template-columns: repeat(auto-fill, minmax(min(135px, 100%), 1fr));
    }
    .table-skeleton {
      @apply tw-h-24;
    }
    .table-choice {
      @apply tw-flex tw-min-h-[100px] tw-flex-col tw-gap-1 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-3 tw-text-left;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
      &.reserved {
        @apply tw-border-dashed tw-border-sky-300 tw-bg-sky-50;
      }
      &.occupied {
        @apply tw-border-amber-200 tw-bg-amber-50;
      }
      &.too_small {
        @apply tw-bg-neutral-100;
      }
      &.selected {
        @apply tw-border-2 tw-border-orange-600 tw-bg-orange-50;
      }
      &:disabled {
        @apply tw-cursor-not-allowed tw-opacity-50;
      }
    }
    .table-name {
      @apply tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-1 tw-font-semibold tw-text-neutral-900;
    }
    .table-capacity {
      @apply tw-text-xs tw-text-neutral-500;
    }
    .table-status {
      @apply tw-mt-auto tw-text-xs tw-font-medium tw-text-neutral-600;
    }
  }
</style>
